import { useCallback, useEffect, useRef, useState } from "react";
import {
  getFirstResultLocationAndAddress,
  getUnifiedSuggestions,
  resolveSuggestionArea,
  UnifiedSuggestItem,
} from "../../apis/search";
import { generateSessionToken } from "./sessionToken";

/** A location the user picked, resolved to coordinates. */
export type SelectedLocation = {
  /** Full label, e.g. "Shoreditch, London". */
  text: string;
  /** Short label, e.g. "Shoreditch". */
  simpleText: string;
  latitude: number;
  longitude: number;
  placeId: string | null;
};

const DEBOUNCE_MS = 300;
const MIN_TERM_LENGTH = 3;

type Options = {
  /** Bias suggestions towards this point. */
  latitude?: number;
  longitude?: number;
};

/**
 * Area/city autosuggest against the unified endpoint's locations scope.
 *
 * One Google autocomplete session spans all keystrokes up to a selection, so the
 * token is generated on first use, sent with every suggest call, reused by the
 * resolve call, then reset.
 */
export function useLocationSuggest(term: string, options: Options = {}) {
  const { latitude, longitude } = options;
  const [items, setItems] = useState<UnifiedSuggestItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const sessionTokenRef = useRef<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a slow earlier request overwriting a newer one.
  const requestIdRef = useRef(0);

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = generateSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = "";
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = term.trim();
    if (trimmed.length < MIN_TERM_LENGTH) {
      requestIdRef.current += 1;
      setItems([]);
      setIsLoading(false);
      return;
    }

    // Loading starts with the request, not the keystroke, so the current list
    // stays put while the user is still typing.
    debounceRef.current = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const list = await getUnifiedSuggestions(trimmed, {
          latitude,
          longitude,
          sessionToken: ensureSessionToken(),
          scope: "locations",
        });
        if (requestId !== requestIdRef.current) return;
        setItems(list);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setItems([]);
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [term, latitude, longitude, ensureSessionToken]);

  /**
   * Legacy text-search resolve. Still needed because resolve-suggestion-area
   * 404s when the Google flag is off. Its own failure mode is to echo back the
   * bias coordinates, which would relabel the map without moving it, so that
   * case is treated as a failure.
   */
  const resolveByText = useCallback(
    async (text: string): Promise<SelectedLocation | null> => {
      const result = await getFirstResultLocationAndAddress(
        text,
        latitude ?? 0,
        longitude ?? 0,
      );
      if (!result || result.error || !result.lat || !result.lng) return null;

      return {
        text: result.address || text,
        simpleText: result.simpleAddress || text,
        latitude: result.lat,
        longitude: result.lng,
        placeId: null,
      };
    },
    [latitude, longitude],
  );

  /** Resolve a suggestion to coordinates and close the billing session. */
  const selectItem = useCallback(
    async (item: UnifiedSuggestItem): Promise<SelectedLocation | null> => {
      const placeId = item.data?.place_id ?? null;
      const label = [item.displayText, item.subtitle].filter(Boolean).join(", ");

      // Degrade mode returns Azure locations, which already carry coordinates.
      if (!placeId) {
        const { latitude: itemLat, longitude: itemLng } = item.data ?? {};
        if (itemLat != null && itemLng != null) {
          return {
            text: label,
            simpleText: item.displayText,
            latitude: itemLat,
            longitude: itemLng,
            placeId: null,
          };
        }
        return resolveByText(label);
      }

      const area = await resolveSuggestionArea({
        placeId,
        sessionToken: sessionTokenRef.current || null,
      });
      resetSessionToken();
      if (!area) return resolveByText(label);

      return {
        text: area.address || label,
        simpleText: area.name || item.displayText,
        latitude: area.lat,
        longitude: area.lng,
        placeId,
      };
    },
    [resetSessionToken, resolveByText],
  );

  /** Resolve free text (the "press enter to search" row) to coordinates. */
  const selectFreeText = useCallback(
    async (text: string): Promise<SelectedLocation | null> => {
      const trimmed = text.trim();
      if (!trimmed) return null;

      const area = await resolveSuggestionArea({
        query: trimmed,
        latitude,
        longitude,
      });
      resetSessionToken();
      if (!area) return resolveByText(trimmed);

      return {
        text: area.address || trimmed,
        simpleText: area.name || trimmed,
        latitude: area.lat,
        longitude: area.lng,
        placeId: area.place_id,
      };
    },
    [latitude, longitude, resetSessionToken, resolveByText],
  );

  return { items, isLoading, selectItem, selectFreeText, resetSessionToken };
}
